<template>
  <v-menu
    v-if="descargas.length > 0"
    v-model="menuAbierto"
    :close-on-content-click="false"
    location="bottom end"
    offset="10"
  >
    <template #activator="{ props: activatorProps }">
      <div v-bind="activatorProps" class="desc-chip" :title="resumenTitulo">
        <v-icon size="18" color="white">mdi-download</v-icon>

        <span class="desc-chip-text">{{ textoChip }}</span>

        <!-- Barra de progreso horizontal con % REAL -->
        <template v-if="hayActivo">
          <div class="desc-bar-track">
            <div class="desc-bar-fill" :style="{ width: progresoGlobal + '%' }"></div>
          </div>
          <span class="desc-chip-pct">{{ progresoGlobal }}%</span>
        </template>

        <span v-if="activas > 1" class="desc-chip-badge">{{ activas }}</span>
      </div>
    </template>

    <!-- Panel detallado -->
    <v-card width="340" class="desc-panel" elevation="8">
      <div class="desc-panel-header">
        <div class="d-flex align-center ga-2">
          <v-icon size="18" color="white">mdi-download-multiple</v-icon>
          <span>Descargas ({{ descargas.length }})</span>
        </div>
        <v-icon size="18" color="white" style="cursor:pointer;"
          @click="menuAbierto = false" title="Cerrar">mdi-close</v-icon>
      </div>

      <div class="desc-panel-body">
        <div v-for="d in descargas" :key="d.id" class="desc-item">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center ga-1">
              <!-- ícono según tipo -->
              <v-icon size="14" :color="d.tipo === 'exportar' ? '#3d8b37' : d.tipo === 'archivo' ? '#1565c0' : '#004d82'">
                {{ d.tipo === 'exportar' ? 'mdi-export' : d.tipo === 'archivo' ? 'mdi-file-download-outline' : 'mdi-application-export' }}
              </v-icon>
              <span class="desc-nombre" :title="d.nombre">{{ d.nombre }}</span>
            </div>
            <v-icon
              v-if="d.estado === 'completado' || d.estado === 'error'"
              size="16" style="cursor:pointer; color:#888;"
              @click="quitarDescarga(d.id)" title="Quitar"
            >mdi-close</v-icon>
          </div>

          <template v-if="d.estado === 'generando'">
            <v-progress-linear :model-value="d.progreso" color="#ce501b" height="6" rounded />
            <span class="desc-estado">Generando… {{ d.progreso }}%</span>
          </template>

          <template v-else-if="d.estado === 'descargando'">
            <v-progress-linear indeterminate color="#004d82" height="6" rounded />
            <span class="desc-estado">Descargando el archivo…</span>
          </template>

          <template v-else-if="d.estado === 'completado'">
            <div class="d-flex align-center ga-1">
              <v-icon size="16" color="success">mdi-check-circle</v-icon>
              <span class="desc-estado" style="color:#2e7d32;">Completado</span>
            </div>
          </template>

          <template v-else-if="d.estado === 'error'">
            <div class="d-flex align-center ga-1">
              <v-icon size="16" color="error">mdi-alert-circle</v-icon>
              <span class="desc-estado" style="color:#c62828;">{{ d.error }}</span>
            </div>
          </template>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useDescargas } from './Usedescargas.js'

const { descargas, quitarDescarga } = useDescargas()
const menuAbierto = ref(false)

const activas = computed(() =>
  descargas.value.filter(d => d.estado === 'generando' || d.estado === 'descargando').length
)

const hayActivo = computed(() =>
  descargas.value.some(d => d.estado === 'generando' || d.estado === 'descargando')
)

const progresoGlobal = computed(() => {
  const activos = descargas.value.filter(d => d.estado === 'generando' || d.estado === 'descargando')
  if (!activos.length) return 0
  return Math.round(activos.reduce((acc, d) => acc + (d.progreso || 0), 0) / activos.length)
})

const textoChip = computed(() => {
  const generando   = descargas.value.some(d => d.estado === 'generando')
  const descargando = descargas.value.some(d => d.estado === 'descargando')
  if (generando)   return 'Generando'
  if (descargando) return 'Descargando'
  const conError = descargas.value.some(d => d.estado === 'error')
  return conError ? 'Con errores' : 'Listo'
})

const resumenTitulo = computed(() =>
  activas.value > 0
    ? `${activas.value} proceso(s) en curso — clic para ver detalle`
    : 'Procesos recientes — clic para ver detalle'
)
</script>

<style scoped>
.desc-chip {
  display: flex; align-items: center; gap: 8px;
  height: 36px; padding: 0 12px; border-radius: 18px;
  cursor: pointer; color: #fff;
  background: rgba(255,255,255,0.12);
  transition: background 0.18s ease; user-select: none;
}
.desc-chip:hover { background: rgba(255,255,255,0.22); }
.desc-chip-text  { font-size: 12.5px; font-weight: 600; white-space: nowrap; }

.desc-bar-track {
  width: 120px; height: 8px; border-radius: 4px;
  background: rgba(255,255,255,0.25); overflow: hidden; flex-shrink: 0;
}
.desc-bar-fill {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #6fd0ff, #ffffff);
  transition: width 0.3s ease;
}
.desc-chip-pct {
  font-size: 12.5px; font-weight: 700;
  white-space: nowrap; min-width: 40px; text-align: right;
}
.desc-chip-badge {
  background: #ce501b; color: #fff; font-size: 11px; font-weight: 700;
  min-width: 18px; height: 18px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; padding: 0 5px;
}

.desc-panel      { border-radius: 8px; overflow: hidden; }
.desc-panel-header {
  background: #004d82; color: #fff;
  padding: 8px 12px; display: flex; align-items: center;
  justify-content: space-between; font-size: 13px; font-weight: 600;
}
.desc-panel-body { max-height: 340px; overflow-y: auto; padding: 4px 0; }

.desc-item { padding: 8px 12px; border-bottom: 1px solid #f0f0f0; }
.desc-item:last-child { border-bottom: none; }
.desc-nombre {
  font-size: 12px; font-weight: 500; color: #333;
  max-width: 240px; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.desc-estado { font-size: 11px; color: #666; display: block; margin-top: 3px; }
</style>